import { skipToken } from "@reduxjs/toolkit/query";
import { usePortalAccount } from "../features/accounts/PortalAccounts";
import { selectPortalAccountScope } from "../features/auth/authSelectors";
import { getApiFailureMessage, getApiFailureName } from "../lib/gboApiClient";
import { useAppSelector } from "../store/hooks";
import {
  useGetPortalInvoiceQuery,
  useDownloadPortalInvoiceMutation,
  useDownloadPortalReceiptMutation,
} from "../store/portalInvoiceApi";
import { getInvoiceViewModel } from "../features/invoices/invoiceViewModel";
import { useInvoiceDownloads } from "../features/invoices/useInvoiceDownloads";

export function useInvoicePage(invoiceCode: string) {
  const scope = useAppSelector(selectPortalAccountScope);
  const account = usePortalAccount();
  const query = useGetPortalInvoiceQuery(scope ? { scope, invoiceCode } : skipToken, {
    refetchOnMountOrArgChange: true,
  });
  const [downloadInvoice] = useDownloadPortalInvoiceMutation();
  const [downloadReceipt] = useDownloadPortalReceiptMutation();
  const downloads = useInvoiceDownloads({
    invoice: (id) => downloadInvoice(id).unwrap(),
    receipt: (id) => downloadReceipt(id).unwrap(),
  });
  const data = query.currentData;
  if (query.isError) {
    if (getApiFailureName(query.error) === "NotFoundError")
      return {
        status: "notFound" as const,
        message: "This invoice is not available for the selected account.",
      };
    return {
      status: "error" as const,
      message: getApiFailureMessage(query.error, "Please try again."),
      retry: () => void query.refetch(),
    };
  }
  if (!data || data.invoice.customerId !== account.customerId)
    return { status: "loading" as const };
  return {
    status: "ready" as const,
    ...downloads,
    downloadInvoice: () => downloads.downloadInvoice(data.invoice.id),
    viewModel: getInvoiceViewModel(data.invoice),
  };
}
